import { useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";
import { useAuth } from "../contexts/AuthContext";
import { fmt, fmt1 } from "../lib/format";
import { useKpi } from "../lib/useKpi";
import { toneOfRatio, toneOfInverse, TONE_HEX } from "../lib/tone";

Chart.register(...registerables);

const MONTH_SHORT = ["Янв","Фев","Мар","Апр","Май","Июн","Июл","Авг","Сен","Окт","Ноя","Дек"];
const RETURNS_NORM = 20;

export function ProgressScreen() {
  const { managerId, period, periodId, t, availableMonths } = useAuth();
  const { kpi } = useKpi(managerId, period, periodId);

  const trendRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const trendDataRef = useRef<Array<number | null>>([]);

  useEffect(() => {
    trendDataRef.current = Array(availableMonths.length).fill(null);
  }, [availableMonths.length]);

  useEffect(() => {
    if (!managerId) return;
    let cancelled = false;
    const promises = availableMonths.map(async (m, idx) => {
      try {
        const url = `${import.meta.env.VITE_API_BASE || ""}/api/kpi?manager_id=${managerId}&period=month&period_id=${m}`;
        const initData = (window as any).Telegram?.WebApp?.initData ?? "";
        const res = await fetch(url, { headers: { "X-Init-Data": initData } });
        if (!res.ok) return;
        const body = await res.json();
        if (!cancelled) {
          trendDataRef.current[idx] = body.kpi.hasData ? body.kpi.pureSales.fact : null;
        }
      } catch { /* ignore */ }
    });
    Promise.all(promises).then(() => {
      if (!cancelled) renderTrend();
    });
    return () => { cancelled = true; };
  }, [managerId, availableMonths]);

  function renderTrend() {
    if (!trendRef.current) return;
    const ctx = trendRef.current.getContext("2d");
    if (!ctx) return;
    const gradient = ctx.createLinearGradient(0, 0, 0, 160);
    gradient.addColorStop(0, "rgba(139, 92, 246, 0.35)");
    gradient.addColorStop(1, "rgba(139, 92, 246, 0.02)");

    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: availableMonths.map(m => MONTH_SHORT[parseInt(m.slice(5, 7)) - 1]),
        datasets: [{
          data: trendDataRef.current,
          borderColor: "#8b5cf6",
          backgroundColor: gradient,
          tension: 0.4, fill: true, borderWidth: 2.5,
          pointBackgroundColor: "#8b5cf6", pointRadius: 4, pointHoverRadius: 6,
        }],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: c => `${fmt(c.parsed.y as number)} грн` } } },
        scales: {
          y: { beginAtZero: true, ticks: { font: { size: 10 }, callback: v => (Number(v) >= 1000 ? `${Number(v)/1000}k` : v) }, grid: { color: "rgba(0,0,0,.04)" } },
          x: { ticks: { font: { size: 11 } }, grid: { display: false } },
        },
      },
    });
  }

  useEffect(() => {
    return () => { chartRef.current?.destroy(); };
  }, []);

  if (!kpi) return null;

  const salesPct = kpi.pureSales.plan > 0 ? (kpi.pureSales.fact / kpi.pureSales.plan) * 100 : 0;
  const salesShown = Math.min(salesPct, 200);
  const salesColor = TONE_HEX[toneOfRatio(salesPct / 100)];
  const retPct = kpi.returns.factPct;
  const retShown = Math.min(retPct, 40);
  const retColor = TONE_HEX[toneOfInverse(retPct, RETURNS_NORM)];

  return (
    <section className="p-4 space-y-4">
      <div className="bg-gradient-to-br from-white to-violet-50 rounded-2xl p-4 border border-violet-100">
        <div className="flex items-center justify-between mb-1">
          <div>
            <div className="text-sm font-semibold text-slate-800">{t("progress_title")}</div>
            <div className="text-xs text-slate-500">{t("progress_sub")}</div>
          </div>
          <div className="text-2xl">✨</div>
        </div>
        <div className="relative mt-2" style={{ height: 160 }}>
          <canvas ref={trendRef} />
        </div>
      </div>

      <div className="space-y-3">
        <div className="bg-gradient-to-br from-white to-indigo-50 rounded-2xl p-3 border border-indigo-100">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">{t("progress_plan_sales")}</div>
            <div>
              <span className={`text-lg font-bold ${salesPct >= 100 ? "text-emerald-600" : "text-rose-600"}`}>{fmt1(salesPct)}%</span>
              <span className="text-[9px] text-slate-500 ml-1">{t("progress_from_plan")}</span>
            </div>
          </div>
          <div className="relative">
            <div className="h-5 bg-slate-100 rounded-md overflow-hidden">
              <div className="h-full transition-[width] duration-300" style={{ width: `${(salesShown/200)*100}%`, background: salesColor }} />
            </div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[2px] h-5 bg-emerald-700 z-10" />
          </div>
          <div className="relative mt-1 mx-1">
            <div className="h-px bg-slate-300" />
            <div className="absolute left-0 -top-1 w-px h-2 bg-slate-400" />
            <div className="absolute left-1/2 -translate-x-1/2 -top-1.5 w-[2px] h-3 bg-emerald-500" />
            <div className="absolute right-0 -top-1 w-px h-2 bg-slate-400" />
            <div className="flex justify-between text-[9px] mt-1">
              <span className="text-slate-400">0%</span>
              <span className="text-emerald-700 font-semibold">100%</span>
              <span className="text-slate-400">200%</span>
            </div>
          </div>
          <div className="text-[10px] text-slate-600 text-center mt-1">
            <span className="font-semibold">{fmt(kpi.pureSales.fact)} грн</span> / {fmt(kpi.pureSales.plan)} грн
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-emerald-50 rounded-2xl p-3 border border-emerald-100">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">{t("progress_returns")}</div>
            <div>
              <span className={`text-lg font-bold ${retPct <= RETURNS_NORM ? "text-emerald-600" : "text-rose-600"}`}>{fmt1(retPct)}%</span>
              <span className="text-[9px] text-slate-500 ml-1">{t("progress_from_sales")}</span>
            </div>
          </div>
          <div className="relative">
            <div className="h-5 bg-slate-100 rounded-md overflow-hidden">
              <div className="h-full transition-[width] duration-300" style={{ width: `${(retShown/40)*100}%`, background: retColor }} />
            </div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[2px] h-5 bg-amber-600 z-10" />
          </div>
          <div className="relative mt-1 mx-1">
            <div className="h-px bg-slate-300" />
            <div className="absolute left-0 -top-1 w-px h-2 bg-slate-400" />
            <div className="absolute left-1/2 -translate-x-1/2 -top-1.5 w-[2px] h-3 bg-amber-500" />
            <div className="absolute right-0 -top-1 w-px h-2 bg-slate-400" />
            <div className="flex justify-between text-[9px] mt-1">
              <span className="text-slate-400">0%</span>
              <span className="text-amber-700 font-semibold">20%</span>
              <span className="text-slate-400">40%</span>
            </div>
          </div>
          <div className="text-[10px] text-slate-600 text-center mt-1">норма ≤ 20%</div>
        </div>
      </div>
    </section>
  );
}
